<template>
  <section class="card lobby-card">
    <div class="lobby-grid">
      <div class="lobby-hero">
        <div class="online-badge">
          <span class="dot" /> 在线 {{ onlineCount.toLocaleString() }}
        </div>
        <h2>排位匹配</h2>
        <p class="hero-text">
          按 Rating 匹配同段对手，落子经服务器校验同步。支持自定义标准棋盘规格。
        </p>
        <ul class="feature-list">
          <li>Rating 制 · 初始 {{ defaultScore }} 分</li>
          <li>单局悔棋最多 {{ maxUndosPerGame }} 次 · 协商和棋</li>
          <li>多区服节点 · 匹配前可选接入区域</li>
        </ul>
      </div>
      <div class="lobby-form">
        <label class="field">
          <span>棋盘规格</span>
          <select
            :value="boardSize"
            class="region-select size-select"
            @change="$emit('update:boardSize', +$event.target.value)"
          >
            <option v-for="s in boardSizes" :key="s" :value="s">
              {{ s }}×{{ s }}
            </option>
          </select>
        </label>
        <label class="field">
          <span>接入区服</span>
          <select
            :value="matchRegion"
            class="region-select"
            @change="$emit('update:matchRegion', $event.target.value)"
          >
            <option v-for="r in regions" :key="r" :value="r">
              {{ r }}
            </option>
          </select>
        </label>
        <label class="field">
          <span>对战昵称</span>
          <div class="name-row">
            <input
              :value="displayName"
              type="text"
              maxlength="16"
              placeholder="输入或随机"
              @input="$emit('update:displayName', $event.target.value.trim())"
              @blur="$emit('persist-profile')"
            />
            <button type="button" class="btn mini" @click="$emit('random-name')">
              随机
            </button>
          </div>
        </label>
        <div class="rating-row" :class="{ blocked: !canMatch }">
          <span>当前 Rating</span>
          <strong>{{ rating }}</strong>
        </div>
        <p v-if="dailyBonusNotice" class="bonus-notice">
          {{ dailyBonusNotice }}
        </p>
        <p v-else-if="!canMatch" class="match-blocked">
          积分不足，无法匹配（需 ≥ {{ minScoreToMatch }}）。积分为 0 时每日登录可领
          {{ dailyBonusWhenZero }} 分。
        </p>
        <button
          type="button"
          class="btn primary wide"
          :disabled="!validSize || !canMatch"
          @click="$emit('match')"
        >
          开始匹配
        </button>
        <p class="fine-print">
          开始匹配将扣除 {{ entryFee }} Rating · 胜局 +{{ winBonus }} · 退出或认输判负 · 单局悔棋
          ≤ {{ maxUndosPerGame }} 次
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import gameConfig, { randomBetween } from '../config/gameConfig'
import { maxUndosPerGame } from '../game/negotiation'

export default {
  name: 'LobbyView',
  props: {
    boardSize: { type: Number, required: true },
    boardSizes: { type: Array, required: true },
    displayName: { type: String, required: true },
    rating: { type: Number, required: true },
    validSize: { type: Boolean, required: true },
    canMatch: { type: Boolean, default: true },
    matchRegion: { type: String, required: true },
    dailyBonusNotice: { type: String, default: '' },
  },
  emits: [
    'update:boardSize',
    'update:displayName',
    'update:matchRegion',
    'match',
    'random-name',
    'persist-profile',
  ],
  data() {
    const rules = gameConfig.rating
    return {
      onlineCount: randomBetween(1280, 4890),
      entryFee: rules.entryFee,
      winBonus: rules.winBonus,
      defaultScore: rules.defaultScore,
      dailyBonusWhenZero: rules.dailyBonusWhenZero ?? 3,
      minScoreToMatch: rules.minScoreToMatch ?? rules.entryFee,
      maxUndosPerGame: maxUndosPerGame(),
      regions: gameConfig.matchmaking.regions ?? [],
    }
  },
  mounted() {
    this._onlineTimer = setInterval(() => {
      const delta = randomBetween(-40, 55)
      this.onlineCount = Math.max(800, Math.min(6200, this.onlineCount + delta))
    }, 4000)
  },
  beforeUnmount() {
    if (this._onlineTimer) clearInterval(this._onlineTimer)
  },
}
</script>

<style scoped>
.card {
  background: rgba(17, 24, 39, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
}

.lobby-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: center;
}

@media (max-width: 768px) {
  .lobby-grid {
    grid-template-columns: 1fr;
  }
}

.online-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: #6ee7b7;
  margin-bottom: 0.75rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.online-badge .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 8px #34d399;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  50% {
    opacity: 0.5;
  }
}

.lobby-hero h2 {
  margin: 0 0 0.75rem;
  font-size: 1.75rem;
}

.hero-text {
  color: #94a3b8;
  line-height: 1.6;
}

.feature-list {
  margin: 1.25rem 0 0;
  padding-left: 1.2rem;
  color: #cbd5e1;
  line-height: 1.9;
}

.field {
  display: block;
  margin-bottom: 1.25rem;
}

.field span {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #94a3b8;
}

.field input[type='text'],
.region-select {
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #f1f5f9;
}

.field input[type='text'] {
  flex: 1;
}

.region-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8'%3E%3Cpath d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}

.name-row {
  display: flex;
  gap: 0.5rem;
}

.size-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.size-control input[type='range'] {
  flex: 1;
}

.size-badge {
  min-width: 4.5rem;
  text-align: center;
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  background: #1e293b;
  font-weight: 600;
}

.rating-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.8);
  font-size: 0.9rem;
  color: #94a3b8;
}

.rating-row strong {
  font-size: 1.25rem;
  color: #ecfdf5;
}

.rating-row.blocked strong {
  color: #fca5a5;
}

.bonus-notice {
  margin: -0.35rem 0 0.85rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.match-blocked {
  margin: -0.35rem 0 0.85rem;
  font-size: 0.78rem;
  color: #fbbf24;
  line-height: 1.45;
}

.btn {
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: transform 0.12s ease, opacity 0.12s ease;
}

.btn:active:not(:disabled) {
  transform: scale(0.98);
}

.btn.primary {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: #fff;
  padding: 0.75rem 1.5rem;
}

.btn.primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn.wide {
  width: 100%;
  padding: 0.85rem;
  font-weight: 600;
}

.btn.mini {
  padding: 0.65rem 0.85rem;
  background: #334155;
  color: #e2e8f0;
  white-space: nowrap;
}

.fine-print {
  margin: 1rem 0 0;
  font-size: 0.72rem;
  color: #64748b;
  text-align: center;
}
</style>